import React, { useState, useEffect, useCallback } from 'react';
import DeviceMetricsPanel from '../components/devices/DeviceMetricsPanel';
import DeviceForm from '../components/devices/DeviceForm';
import { useModal } from '../components/ModalProvider';
import { formatTime } from '../utils/formatTime';
import { PageHeader, Panel, Badge, Button, Tabs } from '../components/design';

const CORE_API = '/api';

export default function DeviceDetail({ deviceId }) {
  const { alert: showAlert, confirm: showConfirm } = useModal();

  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [incidents, setIncidents] = useState([]);
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [enrolling, setEnrolling] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchDevice = useCallback(async () => {
    try {
      const res = await fetch(`${CORE_API}/devices/${deviceId}`);
      if (!res.ok) throw new Error('Device not found');
      const data = await res.json();
      setDevice(data);
    } catch (err) {
      showAlert(err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [deviceId, showAlert]);

  const fetchIncidents = useCallback(async () => {
    try {
      const res = await fetch(`${CORE_API}/devices/${deviceId}/incidents?status=all&limit=100`);
      const data = await res.json();
      setIncidents(data.items || []);
    } catch (err) {
      console.error('Failed to fetch incidents', err);
    }
  }, [deviceId]);

  const fetchVulnerabilities = useCallback(async () => {
    try {
      const res = await fetch(`${CORE_API}/devices/${deviceId}/vulnerabilities?limit=100`);
      const data = await res.json();
      setVulnerabilities(data.items || []);
    } catch (err) {
      console.error('Failed to fetch vulnerabilities', err);
    }
  }, [deviceId]);

  useEffect(() => {
    fetchDevice();
  }, [fetchDevice]);

  useEffect(() => {
    if (tab === 'incidents') fetchIncidents();
    if (tab === 'vulnerabilities') fetchVulnerabilities();
  }, [tab, fetchIncidents, fetchVulnerabilities]);

  const handleSave = async (formData) => {
    setSaving(true);
    try {
      const res = await fetch(`${CORE_API}/devices/${deviceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error('Save failed');
      setEditing(false);
      fetchDevice();
      showAlert('Device updated', 'success');
    } catch (err) {
      showAlert(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEnroll = async () => {
    setEnrolling(true);
    try {
      const res = await fetch(`${CORE_API}/devices/${deviceId}/enroll`, { method: 'POST' });
      const result = await res.json();
      fetchDevice();
      if (result.online) {
        showAlert('Device is online', 'success');
      } else {
        showAlert('Device is offline/unreachable', 'warning');
      }
    } catch (err) {
      showAlert('Enroll failed: ' + err.message, 'error');
    } finally {
      setEnrolling(false);
    }
  };

  const handleDelete = async () => {
    const ok = await showConfirm('Delete this device?', 'This action cannot be undone.');
    if (!ok) return;

    setDeleting(true);
    try {
      const res = await fetch(`${CORE_API}/devices/${deviceId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      showAlert('Device deleted', 'success');
      window.location.hash = 'devices';
    } catch (err) {
      showAlert(err.message, 'error');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <div className="muted" style={{ textAlign: 'center', padding: '40px 0' }}>Loading...</div>;
  }

  if (!device) {
    return (
      <div className="empty">
        <div style={{ fontSize: 40, marginBottom: 10 }}>📡</div>
        <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>Device not found</h3>
        <button className="chip" onClick={() => { window.location.hash = 'devices'; }}>Back to devices</button>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'incidents', label: `Incidents (${device.incident_count || 0})` },
    { id: 'vulnerabilities', label: `CVEs (${device.cve_count || 0})` },
  ];

  const severityBadge = (severity) => {
    const tone = { critical: 'red', high: 'red', medium: 'amber', low: 'blue' }[severity] || 'gray';
    return <Badge tone={tone}>{severity}</Badge>;
  };

  return (
    <div>
      <PageHeader
        crumbs={[{ label: 'Devices', onClick: () => { window.location.hash = 'devices'; } }]}
        title={device.name}
        subtitle={`${device.ip_address} · ${device.vendor}${device.model ? ` · ${device.model}` : ''}`}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Badge tone={device.status === 'online' ? 'green' : device.status === 'offline' ? 'red' : 'gray'}>{device.status || 'pending'}</Badge>
            <Button variant="primary" onClick={handleEnroll} disabled={enrolling}>{enrolling ? 'Enrolling...' : 'Enroll'}</Button>
            <Button variant="ghost" onClick={() => setEditing(!editing)}>{editing ? 'Cancel' : 'Edit'}</Button>
            <Button variant="danger" onClick={handleDelete} disabled={deleting}>{deleting ? 'Deleting...' : 'Delete'}</Button>
          </div>
        }
      />

      <div style={{ marginBottom: 16 }}>
        <DeviceMetricsPanel device={device} />
      </div>

      <Tabs tabs={tabs} active={tab} onChange={setTab} />

      <div style={{ marginTop: 16 }}>
        {tab === 'overview' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
            <Panel title="Device Info">
              <dl style={{ display: 'grid', gap: 10, fontSize: 13, margin: 0 }}>
                <div className="flex between">
                  <dt className="muted">Firmware</dt>
                  <dd style={{ margin: 0, color: 'var(--text-1)' }}>{device.firmware_version || '-'}</dd>
                </div>
                <div className="flex between">
                  <dt className="muted">Type</dt>
                  <dd style={{ margin: 0, color: 'var(--text-1)', textTransform: 'capitalize' }}>{device.device_type || '-'}</dd>
                </div>
                <div className="flex between">
                  <dt className="muted">Serial</dt>
                  <dd className="mono" style={{ margin: 0, color: 'var(--text-1)' }}>{device.serial_number || '-'}</dd>
                </div>
                <div className="flex between">
                  <dt className="muted">Location</dt>
                  <dd style={{ margin: 0, color: 'var(--text-1)' }}>{device.location || '-'}</dd>
                </div>
                <div className="flex between">
                  <dt className="muted">Site</dt>
                  <dd style={{ margin: 0, color: 'var(--text-1)' }}>{device.site || '-'}</dd>
                </div>
              </dl>
            </Panel>
            <Panel title="SNMP Configuration">
              <dl style={{ display: 'grid', gap: 10, fontSize: 13, margin: 0 }}>
                <div className="flex between">
                  <dt className="muted">Version</dt>
                  <dd style={{ margin: 0, color: 'var(--text-1)' }}>SNMPv{device.snmp_version || '2c'}</dd>
                </div>
                <div className="flex between">
                  <dt className="muted">Port</dt>
                  <dd style={{ margin: 0, color: 'var(--text-1)' }}>{device.snmp_port || 161}</dd>
                </div>
                <div className="flex between">
                  <dt className="muted">Community</dt>
                  <dd className="mono" style={{ margin: 0, color: 'var(--text-1)' }}>{device.snmp_community || '***'}</dd>
                </div>
                <div className="flex between">
                  <dt className="muted">Check Method</dt>
                  <dd style={{ margin: 0, color: 'var(--text-1)', textTransform: 'capitalize' }}>{device.check_method === 'ping' ? 'Ping Only' : device.check_method === 'snmp_then_ping' ? 'SNMP then Ping' : 'SNMP'}</dd>
                </div>
                <div className="flex between">
                  <dt className="muted">Last Polled</dt>
                  <dd style={{ margin: 0, color: 'var(--text-1)' }}>{device.last_polled_at ? formatTime(device.last_polled_at) : 'Never'}</dd>
                </div>
              </dl>
            </Panel>
          </div>
        )}

        {tab === 'incidents' && (
          <Panel title="Incidents" sub={`${incidents.length} total`}>
            {incidents.length === 0 ? (
              <div className="empty">
                <div style={{ fontSize: 40, marginBottom: 10 }}>🚨</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>No incidents for this device</h3>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {incidents.map((inc) => (
                  <div key={inc.id} className="flex items-center gap-3" style={{ padding: '12px 16px', borderRadius: 12, background: 'var(--glass)', border: '1px solid var(--glass-border)' }}>
                    {severityBadge(inc.severity)}
                    <span style={{ color: 'var(--text-1)', fontSize: 13, flex: 1 }}>{inc.title}</span>
                    <span className="muted" style={{ fontSize: 12 }}>{formatTime(inc.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        )}

        {tab === 'vulnerabilities' && (
          <Panel title="Vulnerabilities" sub={`${vulnerabilities.length} total`}>
            {vulnerabilities.length === 0 ? (
              <div className="empty">
                <div style={{ fontSize: 40, marginBottom: 10 }}>🛡️</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-1)', marginBottom: 6 }}>No CVEs found for this device</h3>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {vulnerabilities.map((cve) => (
                  <div key={cve.id} className="flex items-center gap-3" style={{ padding: '12px 16px', borderRadius: 12, background: 'var(--glass)', border: '1px solid var(--glass-border)' }}>
                    {severityBadge(cve.severity)}
                    <span className="mono" style={{ fontSize: 13, color: 'var(--accent-1)' }}>{cve.cve_id || cve.advisory_id}</span>
                    <span style={{ color: 'var(--text-1)', fontSize: 13, flex: 1 }}>{cve.title}</span>
                    <span className="muted" style={{ fontSize: 12 }}>{formatTime(cve.created_at)}</span>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        )}
      </div>

      {editing && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--glass-border)', borderRadius: 14, padding: 20, width: '100%', maxWidth: 672, maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-1)', marginBottom: 16 }}>Edit Device</h2>
            <DeviceForm
              initialData={device}
              onSave={handleSave}
              onCancel={() => setEditing(false)}
              loading={saving}
            />
          </div>
        </div>
      )}
    </div>
  );
}
